// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { HitlApproval, HitlApprovalState } from '../../types';
import { DxTag } from './ScreenHelpers';
import { useActiveSite } from '../../hooks/useActiveSite';
import { useSiteTime } from '../../hooks/useSiteTime';
import { formatInZone } from '../../utils/siteTime';
import {
  isDayAheadV1Plan,
  getNumberAttr,
  getStringAttr,
  getPlanTags,
  parseExecutionStart,
} from '../../utils/planAttributes';

const STATUS_LABELS: Record<HitlApprovalState, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
};

/** A pending plan whose day passed was never decided: it reads Expired, not Pending approval. */
const EXPIRED_LABEL = 'Expired';
const EXPIRED_BORDER_COLOR = 'var(--border-strong, var(--text-helper))';

const STATUS_BORDER_COLOR: Record<HitlApprovalState, string> = {
  PENDING: 'var(--interactive)',
  APPROVED: 'var(--st-ok)',
  REJECTED: 'var(--sev1)',
};

function approvalAttr(approval: HitlApproval, key: string): string | undefined {
  const value = approval.attributes?.[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * `executionStart` is a UTC-anchored day boundary (e.g. 2026-07-08T00:00:00+00:00),
 * so it's formatted in UTC — rendering it in local time would slide the label to
 * the previous day for anyone west of Greenwich.
 */
function formatExecutesDate(iso: string | undefined): string | undefined {
  const d = parseExecutionStart(iso);
  if (!d) return undefined;
  return formatInZone(d, 'dateShort', 'UTC', '').toUpperCase() || undefined;
}

/**
 * Savings framing: the card leads with the magnitude of the improvement in
 * green, without a +/- sign — a plan that saves 8.1% reads as "8.1%". The
 * underlying attribute keeps its own sign (energy vs. baseline is stored
 * negative for a reduction); only the display is normalized here.
 */
function formatSavings(value: number, kind: 'percent' | 'currency'): string {
  const magnitude = Math.abs(value);
  return kind === 'percent'
    ? `${magnitude.toLocaleString(undefined, { maximumFractionDigits: 1 })}%`
    : `$${magnitude.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function SavingsStat({
  label,
  value,
  kind,
  note,
}: {
  label: string;
  value: number;
  kind: 'percent' | 'currency';
  note?: string;
}) {
  return (
    <div className="hs-card-stat">
      <span className="hs-card-stat-label">{label}</span>
      <span className="hs-card-stat-value hs-card-stat-value-savings">
        {formatSavings(value, kind)}
      </span>
      {note && <span className="hs-card-stat-note">{note}</span>}
    </div>
  );
}

function StatusPill({ state, expired }: { state: HitlApprovalState; expired?: boolean }) {
  return (
    <span
      className={`hs-card-status hs-card-status-${expired ? 'expired' : state.toLowerCase()}`}
      data-testid="plan-status"
    >
      {expired ? EXPIRED_LABEL : (STATUS_LABELS[state] ?? state)}
    </span>
  );
}

function LegacyCardBody({ approval, expired }: { approval: HitlApproval; expired?: boolean }) {
  const { siteLabel } = useActiveSite();
  const { format } = useSiteTime();
  const title = approvalAttr(approval, 'title') ?? 'Optimization plan';
  const summary = approvalAttr(approval, 'summary');
  const scenarioId = approvalAttr(approval, 'scenarioId');

  return (
    <>
      <div className="hs-card-row">
        <DxTag>Optimization</DxTag>
        <span
          data-testid="plan-status"
          style={{
            marginLeft: 'auto',
            fontSize: 11,
            color: 'var(--text-helper)',
            fontFamily: 'IBM Plex Mono, monospace',
          }}
        >
          {expired ? EXPIRED_LABEL : (STATUS_LABELS[approval.state] ?? approval.state)}
        </span>
      </div>

      <div className="hs-card-asset">{title}</div>
      <div className="hs-card-summary">{summary ?? `Plan for ${siteLabel}`}</div>

      <div className="hs-card-meta">
        {scenarioId && <span>{scenarioId}</span>}
        {approval.createdAt && <span>· created {format(approval.createdAt, 'dateTime')}</span>}
      </div>
    </>
  );
}

/**
 * Card for `optimizationType: "day-ahead"` / `plan_version: "v1"` approvals —
 * the drivers/actions narration.
 *
 * Everything shown here comes from the HITL approval's own attributes bag; the
 * narration file in the object store is deliberately NOT fetched for the list,
 * only once a card is clicked and PlanDetail mounts. Any stat whose attribute
 * is absent or null is omitted rather than rendered as a placeholder — e.g. a
 * null `totalCostSavings` drops "Expected utility bill savings" entirely.
 */
function DayAheadCardBody({
  approval,
  showApprovedOn,
  expired,
}: {
  approval: HitlApproval;
  showApprovedOn?: boolean;
  expired?: boolean;
}) {
  const { format } = useSiteTime();
  const attributes = approval.attributes;
  const title = getStringAttr(attributes, 'title') ?? 'Optimization plan';
  const tags = getPlanTags(attributes);
  const optimizationType = getStringAttr(attributes, 'optimizationType') ?? 'day-ahead';
  const executesDate = formatExecutesDate(getStringAttr(attributes, 'executionStart'));
  // Operators see when the plan was approved (the decision time); the review
  // view leads with when it was generated (the approval's creation time).
  const footerDate = format(
    showApprovedOn ? (approval.updatedAt ?? approval.createdAt) : approval.createdAt,
    'dateTime',
    ''
  );
  const footerLabel = showApprovedOn ? 'Approved on' : 'Generated on';
  const energyVsBaseline = getNumberAttr(attributes, 'energyVsBaselinePct');
  const totalCostSavings = getNumberAttr(attributes, 'totalCostSavings');
  // Absolute energy reduction for the stat's sub-line; stored signed negative
  // for a reduction, so the display normalizes the sign.
  const energyVsBaselineKwh = getNumberAttr(attributes, 'energyVsBaselineKwh');
  const hasStats = energyVsBaseline !== undefined || totalCostSavings !== undefined;

  return (
    <>
      <div className="hs-card-row">
        <span className="hs-card-badge" data-testid="plan-badge">
          {optimizationType.toUpperCase()}
          {executesDate && ` · EXECUTES ${executesDate}`}
        </span>
        <StatusPill state={approval.state} expired={expired} />
      </div>

      {tags.length > 0 && <div className="hs-card-plan-title">{tags.join(' · ')}</div>}

      <p className="hs-card-plan-desc">{title}</p>

      {hasStats && (
        <div className="hs-card-stats">
          {energyVsBaseline !== undefined && (
            <SavingsStat
              label="Energy saved vs. baseline"
              value={energyVsBaseline}
              kind="percent"
              note={
                energyVsBaselineKwh !== undefined
                  ? `${Math.abs(energyVsBaselineKwh).toLocaleString(undefined, {
                      maximumFractionDigits: 0,
                    })} kWh vs. baseline`
                  : undefined
              }
            />
          )}
          {totalCostSavings !== undefined && (
            <SavingsStat
              label="Expected utility bill savings"
              value={totalCostSavings}
              kind="currency"
              note="Est · reconciled monthly."
            />
          )}
        </div>
      )}
      {footerDate && (
        <div className="hs-card-generated">
          {footerLabel} {footerDate}
        </div>
      )}
    </>
  );
}

/**
 * Optimization plan tile shared by the Optimizations screen and the operator's
 * Approved Recommendations screen. Renders the day-ahead narration card, or the
 * legacy body for older approvals, and opens the plan detail on click.
 */
export default function PlanCard({
  approval,
  showApprovedOn,
  expired,
}: {
  approval: HitlApproval;
  /** Footer reads "Approved on <decision time>" instead of "Generated on". */
  showApprovedOn?: boolean;
  /**
   * The plan was never decided before its day passed (Past tab). The card reads
   * "Expired" instead of "Pending approval".
   */
  expired?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Open the plan detail, scoped to the Optimizations section (not Site).
  // The user reviews (Approve/Reject) inside the detail — there are no
  // accept/decline shortcuts on the list itself. Carry the list we came from
  // so the detail's back link returns here rather than guessing via history.
  const openDetail = () =>
    navigate(`/optimizations/${approval.approvalId}`, {
      state: { from: `${location.pathname}${location.search}` },
    });

  return (
    <div
      className="hs-card hs-card-plan"
      role="button"
      data-testid="plan-card"
      data-plan-id={approval.approvalId}
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openDetail();
        }
      }}
      style={{
        borderLeftColor: expired ? EXPIRED_BORDER_COLOR : STATUS_BORDER_COLOR[approval.state],
      }}
    >
      {isDayAheadV1Plan(approval.attributes) ? (
        <DayAheadCardBody approval={approval} showApprovedOn={showApprovedOn} expired={expired} />
      ) : (
        <LegacyCardBody approval={approval} expired={expired} />
      )}
    </div>
  );
}
